import { FC } from "react";

import CodeEditor from "components/CodeEditor";
import CollapsiblePanel from "components/CollapsiblePanel";
import CollapsiblePanelToggleTrigger from "components/CollapsiblePanel/ToggleTrigger";
import { FilesColumnId } from "components/Environment/constants/mountedFilesTable";
import { useCheckFileMaxSize } from "components/Environment/useCheckFileMaxSize";
import { EmptystateLockColored, ErrorCircleColored } from "components/icons/generated";
import { TableContext } from "components/Table/Context";
import { useDrawerVisibilityForId } from "ds/components/DrawerNew/useDrawerVisibilityForId";
import EmptyState from "ds/components/EmptyState";
import tableBodyStyles from "ds/components/Table/Body/styles.module.css";
import TableBodyRow from "ds/components/Table/Row";
import TableCell from "ds/components/Table/Row/Cell";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import { ConfigElementWithSource } from "types/generated";

import DescriptionCell from "../Cells/DescriptionCell";
import NameCell from "../Cells/NameCell";
import SecretCell from "../Cells/SecretCell";
import SourceCell from "../Cells/SourceCell";
import TimestampCell from "../Cells/TimestampCell";
import { VariableCellComponentProps } from "../Cells/types";
import CollapsibleRowContent from "./CollapsibleRowContent";
import styles from "./styles.module.css";

type FilesRowProps = {
  config: ConfigElementWithSource;
  actionsComponent?: FC<VariableCellComponentProps>;
};

const CELLS = {
  [FilesColumnId.EXPAND]: () => (
    <CollapsiblePanelToggleTrigger ariaLevel={3} ariaLabel="File toggle" />
  ),
  [FilesColumnId.NAME]: NameCell,
  [FilesColumnId.DESCRIPTION]: DescriptionCell,
  [FilesColumnId.SECRET]: SecretCell,
  [FilesColumnId.SOURCE]: SourceCell,
  [FilesColumnId.CREATED_AT]: TimestampCell,
  [FilesColumnId.ACTIONS]: undefined,
};

const renderFileContent = (
  isTooLarge: boolean,
  isSecret: boolean,
  isBroken: boolean,
  editorValue: string
) => {
  if (isTooLarge) {
    return (
      <EmptyState
        icon={ErrorCircleColored}
        title="File too large"
        caption="We can't display file preview. File is either larger than 0,5MB or in a wrong format."
      />
    );
  }

  if (isBroken) {
    return (
      <EmptyState
        margin="large 0"
        icon={ErrorCircleColored}
        title="We couldn't load the file."
        caption="Please try again later."
      />
    );
  }

  if (isSecret) {
    return (
      <EmptyState
        margin="large 0"
        icon={EmptystateLockColored}
        title="This is a secret file"
        caption="This means you can not edit it, only overwrite or upload a new one."
      />
    );
  }

  return (
    <CodeEditor
      autoHeight
      maxAutoHeight={500}
      language="json"
      body={editorValue}
      readOnly
      options={{ overviewRulerLanes: 0 }}
    />
  );
};

const FilesRow = ({ config, actionsComponent }: FilesRowProps) => {
  const rowId = config.element.id;

  const [isCollapsed, toggle] = useToggle(true);
  const { visibleColumnIds } = useTypedContext(TableContext);
  const isActive = useDrawerVisibilityForId(rowId);
  const { fileContent, exceedsFileSize, isFileBroken } = useCheckFileMaxSize(
    config.element.value ?? ""
  );

  return (
    <CollapsiblePanel
      variant="section"
      className={tableBodyStyles.tableBody}
      role="rowgroup"
      isCollapsed={isCollapsed}
      onToggle={toggle}
    >
      <TableBodyRow isActive={isActive}>
        {visibleColumnIds.map((id) => {
          const Component = CELLS[id as FilesColumnId] ?? actionsComponent;
          if (!Component) {
            return null;
          }

          const uniqueKey = `${id}-${config.element.id}`;
          return (
            <TableCell className={styles.environmentCell} key={uniqueKey} id={id}>
              <Component
                variable={config.element}
                context={config.context}
                stackDependency={config.stackDependency}
              />
            </TableCell>
          );
        })}
      </TableBodyRow>
      <CollapsibleRowContent
        className={styles.environmentCell}
        cellSpan={visibleColumnIds.length}
        padding="x-large"
      >
        {renderFileContent(exceedsFileSize, config.element.writeOnly, isFileBroken, fileContent)}
      </CollapsibleRowContent>
    </CollapsiblePanel>
  );
};

export default FilesRow;
